<template>
  <div class="inventory-page pk-dota-page">
    <section class="inventory-hero">
      <div class="hero-copy">
        <span>库存</span>
        <h1>饰品背包</h1>
        <p>
          管理打开箱子、箱子战斗和幸运饰品获得的库存。取回申请进入人工审核，处理进度可在取回记录中查看。
        </p>
        <div class="hero-actions">
          <button @click="loggedIn ? selectCurrentPage() : openLogin()">选择当前页</button>
          <button class="ghost" @click="goPath('/Inform')">取回记录</button>
        </div>
      </div>
      <div class="hero-case">
        <span class="case-glow"></span>
        <img src="/img/pizzaking/columns/backpack.png" alt="" />
        <strong>{{ summary.all }}</strong>
        <em>库存饰品</em>
      </div>
    </section>

    <section v-if="!loggedIn" class="inventory-auth-card">
      <div class="inventory-auth-copy">
        <span>需要登录</span>
        <strong>登录后查看你的饰品背包</strong>
        <p>背包、回收和取回记录均与当前账户关联。登录后可查看库存，并提交可用饰品的回收或取回申请。</p>
        <button @click="openLogin">登录 / 注册</button>
      </div>
      <div class="inventory-auth-visual">
        <img src="/img/pizzaking/columns/backpack.png" alt="" />
      </div>
    </section>

    <template v-else>
      <section class="inventory-stats">
        <article>
          <span>当前筛选</span>
          <strong>{{ total }} 件</strong>
        </article>
        <article>
          <span>可回收</span>
          <strong>{{ summary.available }} 件</strong>
        </article>
        <article>
          <span>取回中</span>
          <strong>{{ summary.retrieving }} 件</strong>
        </article>
        <article>
          <span>已选择价值</span>
          <strong>{{ checkedPrice }} <i></i></strong>
        </article>
      </section>

      <section v-if="specialKeys.length" class="inventory-keys">
        <header>
          <div>
            <span>钥匙库存</span>
            <strong>专属钥匙</strong>
          </div>
          <em>钥匙仅用于已配置的专属钥匙箱</em>
        </header>
        <article v-for="item in specialKeys" :key="item.key_id">
          <img v-if="item.image_url" :src="item.image_url" alt="" />
          <b v-else>钥匙</b>
          <div><strong>{{ item.name }}</strong><span>{{ item.description || "专属箱子开启凭证" }}</span></div>
          <em>x{{ item.quantity }}</em>
        </article>
      </section>

      <section v-if="battleProps.length" class="inventory-keys inventory-props">
        <header>
          <div>
            <span>对战道具</span>
            <strong>对战道具</strong>
          </div>
          <em>可在已加入的对战房间内发送给其他玩家</em>
        </header>
        <article v-for="item in battleProps" :key="item.prop_id">
          <img v-if="item.image_url" :src="item.image_url" alt="" />
          <b v-else>{{ item.name.slice(0, 1) }}</b>
          <div><strong>{{ item.name }}</strong><span>{{ item.description || "对战房间互动道具" }}</span></div>
          <div class="prop-quantity"><span :class="propStateClass(item)">{{ propStateText(item) }}</span><em>x{{ item.quantity }}</em></div>
        </article>
      </section>

      <section class="inventory-tabs">
        <button
          v-for="tab in inventoryTabs"
          :key="tab.key"
          :class="{ active: currentFilter == tab.key }"
          @click="changeFilter(tab.key)"
        >
          <span>{{ tab.name }}</span>
          <strong>{{ summary[tab.summaryKey] || 0 }}</strong>
        </button>
      </section>

      <section class="inventory-toolbar">
        <div>
          <span class="toolbar-dot"></span>
          <strong>{{ currentTabName }}</strong>
          <em>可回收饰品支持批量回收；取回申请会进入人工审核流程</em>
        </div>
        <div class="toolbar-actions">
          <button @click="selectCurrentPage">全选本页</button>
          <button @click="clearSelected">取消选择</button>
          <button class="primary" :disabled="loading" @click="exchangeSelected">
            <i v-if="loading" class="el-icon-loading"></i>
            回收已选
          </button>
        </div>
      </section>

      <section v-if="listLoading" class="inventory-list-loading">
        <i class="el-icon-loading"></i>
        <strong>正在加载饰品</strong>
        <span>数量较多时会自动分页显示</span>
      </section>

      <section class="inventory-grid" v-else-if="list.length">
        <article
          v-for="(item, index) in list"
          :key="item.id"
          class="skin-card"
          data-pk-sound-click
          :class="{ active: item.state, pending: !item.can_exchange }"
          @click="toggleSkin(item)"
        >
          <div class="skin-status" v-if="!item.can_exchange">{{ item.status_name }}</div>
          <button v-if="item.can_exchange" class="check-mark" :class="{ active: item.state }" @click.stop="toggleSkin(item)">
            <span></span>
          </button>
          <div class="skin-card-meta">
            <span class="skin-source" :class="'source-way-' + item.way">{{ item.way_name || "其它来源" }}</span>
            <span class="skin-rarity">{{ item.exteriorName || "库存饰品" }}</span>
          </div>
          <div class="skin-art">
            <span></span>
            <img :src="item.img" alt="" @error="useSkinFallback($event, index)" />
          </div>
          <div class="skin-info">
            <strong :title="item.name">{{ item.name }}</strong>
            <div class="skin-price">
              <span>{{ priceText(item.price) }}<i aria-hidden="true"></i></span>
            </div>
            <div class="skin-validity" :class="{ expired: item.retrieve_validity_days <= 0 }">
              {{ item.retrieve_validity_label }}
            </div>
            <div v-if="item.delivery_review_remark" class="skin-validity expired">上次退回理由：{{ item.delivery_review_remark }}</div>
          </div>
          <div class="skin-actions">
            <button :class="{ disabled: !item.can_exchange }" @click.stop="exchangeSingle(item)">回收</button>
            <button :class="{ disabled: !item.can_retrieve }" :disabled="!item.can_retrieve || retrievingId == item.id" @click.stop="requestRetrieve(item)">
              {{ retrievingId == item.id ? "提交中" : "取回" }}
            </button>
          </div>
        </article>
      </section>

      <section v-else class="empty-inventory">
        <img src="/img/pizzaking/columns/backpack.png" alt="" />
        <strong>{{ emptyTitle }}</strong>
        <p>{{ emptyText }}</p>
        <button @click="goPath('/Index')">去打开箱子</button>
      </section>

      <el-pagination
        v-if="total > pageSize"
        class="inventory-page-nav"
        background
        :pager-count="5"
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        @current-change="currentChange"
      ></el-pagination>

      <section class="delivery-panel">
        <div>
          <span>发货</span>
          <strong>人工取回审核已开放</strong>
          <p>
            提交申请后饰品将进入“处理中”，工作人员会人工确认后续步骤，并在取回记录中更新进度。
          </p>
        </div>
        <button @click="goPath('/Inform')">查看取回记录</button>
      </section>

      <section class="inventory-faq">
        <header>
          <span>常见问题</span>
          <strong>回收与取回说明</strong>
        </header>
        <article v-for="item in inventoryFaq" :key="item.title">
          <b>{{ item.no }}</b>
          <div>
            <strong>{{ item.title }}</strong>
            <p>{{ item.text }}</p>
          </div>
        </article>
      </section>
    </template>
  </div>
</template>

<script>
import Utils from "./../assets/js/util.js";
import { playUiSound } from "@/utils/pizzaSound";

export default {
  props: ["yidongmenu", "showNav", "openHongbao"],
  data() {
    return {
      loading: false,
      listLoading: false,
      total: 0,
      page: 1,
      pageSize: 24,
      list: [],
      specialKeys: [],
      battleProps: [],
      currentFilter: "available",
      inventoryTabs: [
        { key: "available", summaryKey: "available", name: "可回收" },
        { key: "retrieving", summaryKey: "retrieving", name: "取回中" },
        { key: "exchanged", summaryKey: "exchanged", name: "已回收" },
        { key: "locked", summaryKey: "locked", name: "已取回" },
        { key: "all", summaryKey: "all", name: "全部" },
      ],
      inventoryFaq: [
        { no: "01", title: "可回收饰品", text: "仅“可回收”状态的饰品支持回收，回收后会按页面提示计入小麦余额。" },
        { no: "02", title: "取回记录", text: "提交人工取回后，饰品会进入处理中，处理进度可在消息通知的“取回记录”中查看。" },
        { no: "03", title: "取回说明", text: "获得饰品后会显示取回提示期；提示期结束后仍可提交人工审核，具体进度以取回记录为准。" },
      ],
      summary: {
        all: 0,
        available: 0,
        retrieving: 0,
        exchanged: 0,
        locked: 0,
      },
      checkedPrice: "0.00",
      retrievingId: 0,
      testMode: false,
      skinFallbackImages: [
        require("../assets/hotpizza/hp-ak-green.png"),
        require("../assets/hotpizza/hp-m4-blue.png"),
        require("../assets/hotpizza/hp-mp9-red.png"),
        require("../assets/hotpizza/hp-awp-blue.png"),
        require("../assets/hotpizza/hp-gloves.png"),
      ],
    };
  },
  computed: {
    loggedIn() {
      return Boolean(this.$store.state.token || localStorage.getItem("token"));
    },
    selectedCount() {
      return this.list.filter((item) => item.state).length;
    },
    pageValue() {
      const value = this.list.reduce((sum, item) => sum + Number(item.price || 0), 0);
      return value.toFixed(2);
    },
    currentTabName() {
      const tab = this.inventoryTabs.find((item) => item.key == this.currentFilter);
      return tab ? tab.name + "饰品" : "库存饰品";
    },
    emptyTitle() {
      return this.currentFilter == "available" ? "暂无可回收饰品" : "当前状态暂无记录";
    },
    emptyText() {
      return this.currentFilter == "available"
        ? "打开箱子或箱子战斗获得饰品后，会自动进入背包列表。"
        : "切换到其它状态，或完成新的打开箱子、箱子战斗后再查看。";
    },
  },
  watch: {
    loggedIn(value) {
      if (value) {
        this.getList(this.page);
        this.getSpecialKeys();
        this.getBattleProps();
        this.loadTestFlow();
      } else {
        this.total = 0;
        this.list = [];
        this.specialKeys = [];
        this.battleProps = [];
        this.summary = {
          all: 0,
          available: 0,
          retrieving: 0,
          exchanged: 0,
          locked: 0,
        };
        this.checkedPrice = "0.00";
        this.testMode = false;
      }
    },
  },
  mounted() {
    if (this.loggedIn) {
      this.getList(this.page);
      this.getSpecialKeys();
      this.getBattleProps();
      this.loadTestFlow();
    }
    Utils.$on("special-key", this.getSpecialKeys);
  },
  beforeDestroy() {
    Utils.$off("special-key", this.getSpecialKeys);
  },
  methods: {
    openLogin() {
      this.$store.commit("getLogin", true);
    },
    loadTestFlow() {
      this.$axios.post("/index/Test_flow/status", this.$qs.stringify({})).then((res) => {
        this.testMode = res.data.status == 1 && res.data.data.can_simulate_retrieve == 1;
      });
    },
    getList(page) {
      this.page = page;
      this.listLoading = true;
      return this.$axios
        .post(
          "/index/User/packageListnew",
          this.$qs.stringify({
            page,
            pageSize: this.pageSize,
            status_filter: this.currentFilter,
          })
        )
        .then((res) => {
          const data = res.data;
          if (data.status == 1) {
            this.total = data.data.total;
            this.summary = data.data.summary;
            this.list = data.data.skinList.map((item, index) => {
              item.img = this.stableImage(item.img, this.skinFallbackImages[index % this.skinFallbackImages.length]);
              item.state = false;
              item.can_exchange = item.can_exchange == 1;
              return item;
            });
            if (this.list.length == 0 && this.page > 1) {
              return this.getList(this.page - 1);
            }
          } else {
            this.total = 0;
            this.list = [];
          }
          this.refreshSelectedPrice();
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    getSpecialKeys() {
      this.$axios.post("/index/special_key/myKeys", this.$qs.stringify({}))
        .then((res) => {
          if (res.data.status == 1) {
            this.specialKeys = res.data.data || [];
          }
        });
    },
    getBattleProps() {
      this.$axios.post("/index/battle_prop/myProps", this.$qs.stringify({}))
        .then((res) => {
          if (res.data.status == 1) {
            this.battleProps = res.data.data || [];
          }
        });
    },
    currentChange(val) {
      this.getList(val);
    },
    changeFilter(key) {
      if (this.currentFilter == key) {
        return;
      }
      this.currentFilter = key;
      this.checkedPrice = "0.00";
      this.getList(1);
    },
    toggleSkin(item) {
      if (!item.can_exchange) {
        this.stateNotice(item);
        return;
      }
      item.state = !item.state;
      this.refreshSelectedPrice();
      this.$forceUpdate();
    },
    selectCurrentPage() {
      this.list.forEach((item) => {
        if (item.can_exchange) {
          item.state = true;
        }
      });
      this.refreshSelectedPrice();
      this.$forceUpdate();
    },
    clearSelected() {
      this.list.forEach((item) => {
        item.state = false;
      });
      this.refreshSelectedPrice();
      this.$forceUpdate();
    },
    exchangeSelected() {
      const ids = this.list.filter((item) => item.state).map((item) => item.id);
      if (!ids.length) {
        this.$message({
          showClose: true,
          message: "请先选择要回收的饰品",
          type: "warning",
        });
        return;
      }
      this.exchangeSkins(ids);
    },
    exchangeSingle(item) {
      if (!item.can_exchange) {
        this.stateNotice(item);
        return;
      }
      this.exchangeSkins([item.id]);
    },
    exchangeSkins(ids) {
      this.loading = true;
      this.$axios
        .post(
          "/index/User/exchangeToMoneynew",
          this.$qs.stringify({
            player_skins_ids: ids,
          })
        )
        .then((res) => {
          const data = res.data;
          this.loading = false;
          if (data.status == 1) {
            playUiSound("sale");
            this.$store.commit("getMasonry", data.data.total_amount);
            Utils.$emit("masonry", data.data.total_amount);
            this.$message({
              showClose: true,
              message: data.msg,
              type: "success",
            });
            this.getList(this.page);
          } else {
            this.$message({
              showClose: true,
              message: data.msg,
              type: "warning",
            });
          }
        });
    },
    requestRetrieve(item) {
      if (!item.can_retrieve || this.retrievingId) {
        return;
      }
      this.retrievingId = item.id;
      const endpoint = this.testMode ? "/index/Test_flow/simulateRetrieve" : "/index/User/requestManualRetrieve";
      this.$axios.post(endpoint, this.$qs.stringify({
        player_skins_id: item.id,
      })).then((res) => {
        this.retrievingId = 0;
        if (res.data.status == 1) {
          this.$message({ showClose: true, message: res.data.msg, type: "success" });
          this.$root.$emit("notice-refresh");
          this.getList(this.page);
          return;
        }
        this.$message({ showClose: true, message: res.data.msg, type: "warning" });
      }).catch(() => {
        this.retrievingId = 0;
      });
    },
    stateNotice(item) {
      this.$message({
        showClose: true,
        message: (item.status_name || "当前状态") + "的饰品不能回收。",
        type: "warning",
      });
    },
    refreshSelectedPrice() {
      const value = this.list
        .filter((item) => item.state)
        .reduce((sum, item) => sum + Number(item.price || 0), 0);
      this.checkedPrice = value.toFixed(2);
    },
    priceText(price) {
      return Number(price || 0).toFixed(2);
    },
    propStateText(item) {
      return item.status_name || item.state_name || (item.expire_at ? "有效期内" : "可使用");
    },
    propStateClass(item) {
      const state = String(item.status || item.state || "").toLowerCase();
      return { expired: state === "expired" || state === "used", active: state !== "expired" && state !== "used" };
    },
    stableImage(value, fallback) {
      if (!value) return fallback;
      if (value.indexOf("http://") === 0) return fallback;
      if (value.indexOf("https://img.zbt.com") === 0) return fallback;
      if (value.indexOf("csgoapi.akaifa998.icu") !== -1) return fallback;
      if (value.indexOf("standoffi.oss-cn-hongkong.aliyuncs.com") !== -1) return fallback;
      return value;
    },
    useSkinFallback(event, index) {
      event.target.src = this.skinFallbackImages[index % this.skinFallbackImages.length];
    },
    goPath(path) {
      this.$router.push(path);
    },
  },
};
</script>

<style lang="less" scoped>
.inventory-page {
  min-height: calc(100vh - 198px);
  padding: 26px 26px 74px;
  color: #f5f7ff;
  background:
    radial-gradient(circle at 48% 0, rgba(255, 138, 31, 0.16), transparent 34%),
    radial-gradient(circle at 88% 24%, rgba(0, 217, 255, 0.1), transparent 28%),
    linear-gradient(180deg, #1d202c 0%, #171923 100%);
}

button {
  font-family: inherit;
}

.inventory-hero,
.inventory-auth-card,
.inventory-stats,
.inventory-tabs,
.inventory-toolbar,
.inventory-grid,
.inventory-list-loading,
.empty-inventory,
.delivery-panel,
.inventory-faq {
  max-width: 1420px;
  margin-left: auto;
  margin-right: auto;
}

.inventory-hero {
  min-height: 310px;
  padding: 42px 56px;
  display: grid;
  grid-template-columns: 1.24fr 0.76fr;
  gap: 30px;
  align-items: center;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 132, 31, 0.32);
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(39, 42, 58, 0.96), rgba(24, 26, 36, 0.98)),
    url("../assets/new/task_bg.png") center / cover;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.36);
}

.inventory-hero::before {
  content: "";
  width: 680px;
  height: 240px;
  position: absolute;
  right: 40px;
  bottom: -96px;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255, 181, 73, 0.2), transparent 70%);
}

.inventory-hero::after {
  content: "";
  width: 600px;
  height: 600px;
  position: absolute;
  right: -170px;
  top: -190px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 147, 31, 0.31), transparent 66%);
}

.hero-copy,
.hero-case {
  position: relative;
  z-index: 1;
}

.hero-copy span,
.delivery-panel span {
  color: #ff8a1f;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.hero-copy h1 {
  margin: 12px 0 16px;
  font-size: 58px;
  line-height: 1;
  font-weight: 1000;
}

.hero-copy p {
  max-width: 640px;
  color: #aeb6c8;
  font-size: 17px;
  line-height: 1.8;
}

.hero-actions,
.toolbar-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.hero-actions {
  margin-top: 24px;
}

.hero-actions button,
.inventory-auth-card button,
.toolbar-actions button,
.delivery-panel button,
.empty-inventory button {
  height: 44px;
  padding: 0 22px;
  color: #151620;
  background: linear-gradient(180deg, #ffcb28, #ff8a00);
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 1000;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(241, 107, 0, 0.25);
}

.hero-actions .ghost,
.toolbar-actions button {
  color: #f4f7ff;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: none;
}

.toolbar-actions .primary {
  color: #151620;
  background: linear-gradient(180deg, #ffcb28, #ff8a00);
  border: 0;
  box-shadow: 0 12px 28px rgba(241, 107, 0, 0.22);
}

.toolbar-actions .primary:disabled {
  cursor: wait;
  opacity: 0.76;
}

.inventory-auth-card {
  margin-top: 16px;
  min-height: 332px;
  padding: 42px 52px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 28px;
  align-items: center;
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(43, 45, 59, 0.96), rgba(26, 28, 39, 0.98)),
    radial-gradient(circle at 80% 48%, rgba(255, 138, 31, 0.22), transparent 42%);
  border: 1px solid rgba(255, 132, 31, 0.34);
  border-radius: 18px;
  box-shadow: 0 24px 78px rgba(0, 0, 0, 0.34), inset 0 1px 0 rgba(255, 224, 170, 0.08);
}

.inventory-auth-card::before {
  content: "";
  width: 620px;
  height: 260px;
  position: absolute;
  right: -80px;
  bottom: -120px;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255, 190, 67, 0.28), transparent 68%);
}

.inventory-auth-copy,
.inventory-auth-visual {
  position: relative;
  z-index: 1;
}

.inventory-auth-copy span {
  color: #ff8a1f;
  font-size: 13px;
  font-weight: 1000;
  letter-spacing: 0.16em;
}

.inventory-auth-copy strong {
  display: block;
  margin-top: 12px;
  color: #f8fbff;
  font-size: 36px;
  line-height: 1.15;
  font-weight: 1000;
}

.inventory-auth-copy p {
  max-width: 650px;
  margin: 14px 0 22px;
  color: #aeb6c8;
  font-size: 15px;
  line-height: 1.8;
}

.inventory-auth-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}

.inventory-auth-visual img {
  width: 286px;
  filter:
    drop-shadow(0 30px 34px rgba(0, 0, 0, 0.44))
    drop-shadow(0 0 28px rgba(255, 138, 0, 0.32));
  animation: caseFloat 3.2s ease-in-out infinite;
}

.hero-case {
  min-height: 270px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.hero-case .case-glow {
  width: 430px;
  height: 210px;
  position: absolute;
  bottom: 18px;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255, 194, 71, 0.34), transparent 68%);
  filter: blur(1px);
}

.hero-case img {
  width: 285px;
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 28px 34px rgba(255, 124, 0, 0.22));
  animation: caseFloat 3.2s ease-in-out infinite;
}

.hero-case strong {
  margin-top: -4px;
  color: #ffd764;
  font-size: 44px;
  line-height: 1;
  font-weight: 1000;
  position: relative;
  z-index: 1;
}

.hero-case em {
  margin-top: 7px;
  color: #aab3c6;
  font-style: normal;
  font-weight: 900;
  position: relative;
  z-index: 1;
}

.inventory-stats {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.inventory-stats article,
.inventory-tabs,
.inventory-toolbar,
.skin-card,
.empty-inventory,
.delivery-panel {
  background: linear-gradient(180deg, rgba(43, 45, 59, 0.95), rgba(31, 33, 45, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.inventory-stats article {
  min-height: 106px;
  padding: 20px 22px;
  position: relative;
  overflow: hidden;
}

.inventory-stats article::after {
  content: "";
  width: 150px;
  height: 150px;
  position: absolute;
  right: -76px;
  top: -78px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 138, 31, 0.18), transparent 68%);
}

.inventory-stats span,
.inventory-toolbar em {
  display: block;
  color: #9aa3b8;
  font-size: 13px;
  font-weight: 900;
}

.inventory-stats strong {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #f8fbff;
  font-size: 27px;
  font-weight: 1000;
}

.inventory-stats i,
.skin-price i {
  width: 18px;
  height: 18px;
  display: inline-block;
  background: url("../assets/pizzaking/pizza-token.svg") center / contain no-repeat;
}

.inventory-tabs {
  margin-top: 16px;
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.inventory-keys {
  margin: 16px auto 0;
  padding: 18px 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  background: linear-gradient(180deg, rgba(43, 45, 59, 0.95), rgba(31, 33, 45, 0.98));
  border: 1px solid rgba(255, 177, 47, 0.22);
  border-radius: 12px;
}

.inventory-keys header,
.inventory-keys article {
  display: flex;
  align-items: center;
  gap: 12px;
}

.inventory-keys header { justify-content: space-between; }
.inventory-keys header span,
.inventory-keys article span { display: block; color: #9aa3b8; font-size: 12px; }
.inventory-keys header strong { display: block; color: #f4f7ff; font-size: 18px; }
.inventory-keys header em { color: #ffbd42; font-size: 12px; font-style: normal; }
.inventory-keys article { min-height: 54px; padding: 9px 12px; background: rgba(15, 16, 24, 0.24); border-radius: 8px; }
.inventory-keys article img,
.inventory-keys article b { width: 42px; height: 42px; flex: 0 0 42px; object-fit: contain; }
.inventory-keys article b { display: grid; place-items: center; color: #181923; background: linear-gradient(135deg, #ffdc65, #ff8a00); border-radius: 8px; font-size: 10px; }
.inventory-keys article div { min-width: 0; flex: 1; }
.inventory-keys article strong { display: block; color: #f8fbff; font-size: 14px; }
.inventory-keys article > em { color: #ffd764; font-size: 20px; font-style: normal; font-weight: 1000; }

.inventory-props {
  border-color: rgba(74, 212, 255, 0.2);
  background: linear-gradient(135deg, rgba(29, 56, 73, 0.92), rgba(28, 31, 45, 0.96));
}

.inventory-props header em,
.inventory-props article > em {
  color: #6ee6ff;
}

.inventory-props article b {
  color: #14242b;
  background: linear-gradient(135deg, #73f0ff, #28a9d7);
}

.prop-quantity { min-width: 76px; display: flex; flex-direction: column; align-items: flex-end; gap: 5px; }
.prop-quantity span { padding: 3px 7px; border-radius: 4px; color: #69e0bd; font-size: 11px; font-weight: 800; background: rgba(67, 202, 157, .12); }
.prop-quantity span.expired { color: #b4b8c4; background: rgba(160, 166, 181, .12); }
.prop-quantity em { margin: 0 !important; }

.inventory-tabs button {
  min-height: 62px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #aab3c6;
  background: #242735;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  cursor: pointer;
}

.inventory-tabs button.active {
  color: #151620;
  background: linear-gradient(180deg, #ffcb28, #ff8a00);
  border-color: rgba(255, 224, 116, 0.72);
  box-shadow: 0 14px 30px rgba(255, 138, 0, 0.22);
}

.inventory-tabs span {
  font-size: 14px;
  font-weight: 1000;
}

.inventory-tabs strong {
  font-size: 22px;
  font-weight: 1000;
}

.inventory-toolbar {
  margin-top: 18px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.inventory-toolbar > div:first-child {
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 5px 10px;
  align-items: center;
}

.toolbar-dot {
  width: 10px;
  height: 10px;
  display: block;
  grid-row: span 2;
  border-radius: 50%;
  background: #ff8a00;
  box-shadow: 0 0 18px rgba(255, 138, 0, 0.7);
}

.inventory-toolbar strong {
  color: #f4f7ff;
  font-size: 18px;
  font-weight: 1000;
}

.inventory-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(218px, 1fr));
  gap: 14px;
}

.skin-card {
  min-height: 300px;
  padding: 14px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.skin-card::before {
  content: "";
  width: 280px;
  height: 180px;
  position: absolute;
  left: 50%;
  top: 46px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(154, 27, 38, 0.48), transparent 66%);
  opacity: 0.72;
}

.skin-card::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.028), transparent 45%);
  pointer-events: none;
}

.skin-card:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 138, 0, 0.48);
  box-shadow: 0 22px 58px rgba(0, 0, 0, 0.3), 0 0 22px rgba(255, 138, 0, 0.12);
}

.skin-card.active {
  border-color: #ff9e00;
  box-shadow: inset 0 0 0 1px rgba(255, 224, 116, 0.22), 0 0 28px rgba(255, 138, 0, 0.16);
}

.skin-card.pending {
  opacity: 0.64;
  cursor: default;
}

.skin-status {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #fff;
  font-size: 20px;
  font-weight: 1000;
  background:
    radial-gradient(circle at 50% 48%, rgba(255, 138, 0, 0.18), transparent 42%),
    rgba(0, 0, 0, 0.6);
  letter-spacing: 0.06em;
}

.check-mark {
  width: 28px;
  height: 28px;
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 4;
  display: grid;
  place-items: center;
  padding: 0;
  background: rgba(20, 22, 31, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
}

.check-mark span {
  width: 12px;
  height: 12px;
  display: block;
  border-radius: 4px;
  background: transparent;
}

.check-mark.active span {
  background: linear-gradient(180deg, #ffcb28, #ff8a00);
  box-shadow: 0 0 14px rgba(255, 138, 0, 0.6);
}

.skin-card-meta {
  position: relative;
  z-index: 6;
  min-width: 0;
  min-height: 28px;
  padding-right: 34px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.skin-source {
  flex: 0 0 auto;
  max-width: 92px;
  padding: 4px 8px;
  color: #161822;
  background: linear-gradient(180deg, #ffd65c, #ff970f);
  border: 1px solid rgba(255, 223, 119, 0.72);
  border-radius: 5px;
  box-shadow: 0 5px 16px rgba(255, 138, 0, 0.18);
  font-size: 11px;
  font-weight: 1000;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.skin-source.source-way-2 {
  color: #fff;
  background: linear-gradient(180deg, #ff6e61, #d83d3d);
  border-color: rgba(255, 145, 130, 0.74);
}

.skin-source.source-way-3,
.skin-source.source-way-6 {
  color: #fff;
  background: linear-gradient(180deg, #9b7cff, #6850d8);
  border-color: rgba(190, 174, 255, 0.68);
}

.skin-source.source-way-4 {
  color: #10251c;
  background: linear-gradient(180deg, #65e3a6, #2ebf79);
  border-color: rgba(150, 243, 197, 0.68);
}

.skin-source.source-way-5 {
  color: #fff;
  background: linear-gradient(180deg, #4aaef8, #2878cf);
  border-color: rgba(139, 204, 255, 0.68);
}

.skin-rarity {
  min-width: 0;
  flex: 1 1 auto;
  color: #ff9e27;
  font-size: 12px;
  font-weight: 1000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.skin-art {
  height: 150px;
  margin-top: 10px;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.skin-art span {
  width: 160px;
  height: 95px;
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255, 38, 54, 0.22), transparent 70%);
}

.skin-art img {
  max-width: 170px;
  max-height: 128px;
  object-fit: contain;
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 18px 20px rgba(0, 0, 0, 0.45));
}

.skin-info {
  min-height: 58px;
  position: relative;
  z-index: 2;
}

.skin-info strong {
  min-height: 36px;
  display: block;
  color: #f3f6ff;
  font-size: 14px;
  line-height: 1.35;
  font-weight: 1000;
  text-align: center;
}

.skin-price {
  margin-top: 9px;
  display: flex;
  justify-content: center;
}

.skin-price span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ffd764;
  font-size: 18px;
  font-weight: 1000;
}

.skin-validity {
  margin-top: 7px;
  color: #72d7b1;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.35;
  text-align: center;
}

.skin-validity.expired {
  color: #f0a557;
}

.skin-actions {
  margin-top: 13px;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.skin-actions button {
  height: 38px;
  color: #151620;
  background: linear-gradient(180deg, #ffcb28, #ff8a00);
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 1000;
  cursor: pointer;
}

.skin-actions .disabled {
  color: #8993a6;
  background: #333747;
  border: 1px solid #444b60;
}

.empty-inventory {
  margin-top: 16px;
  min-height: 320px;
  padding: 42px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.inventory-list-loading {
  min-height: 320px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9aa3b8;
  border: 1px solid rgba(255, 174, 29, 0.18);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(43, 45, 59, 0.95), rgba(31, 33, 45, 0.98));
}

.inventory-list-loading i {
  color: #ffae1d;
  font-size: 38px;
}

.inventory-list-loading strong {
  margin-top: 18px;
  color: #f4f7ff;
  font-size: 22px;
}

.inventory-list-loading span {
  margin-top: 8px;
  font-size: 14px;
}

.empty-inventory img {
  width: 132px;
  filter: drop-shadow(0 18px 24px rgba(255, 138, 0, 0.18));
}

.empty-inventory strong {
  margin-top: 18px;
  color: #f4f7ff;
  font-size: 24px;
  font-weight: 1000;
}

.empty-inventory p {
  margin: 8px 0 18px;
  color: #9aa3b8;
  font-size: 14px;
}

.inventory-page-nav {
  max-width: 1420px;
  margin: 24px auto 0;
  text-align: center;
}

.inventory-page-nav /deep/ .el-pager li:not(.disabled).active {
  color: #151620;
  background: #ffae1d;
}

.inventory-page-nav /deep/ .el-pagination.is-background .btn-next,
.inventory-page-nav /deep/ .el-pagination.is-background .btn-prev,
.inventory-page-nav /deep/ .el-pagination.is-background .el-pager li {
  color: #aab3c6;
  background: #242735;
}

.delivery-panel {
  margin-top: 22px;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.delivery-panel strong {
  display: block;
  margin-top: 8px;
  color: #f4f7ff;
  font-size: 22px;
  font-weight: 1000;
}

.delivery-panel p {
  max-width: 820px;
  margin-top: 8px;
  color: #9aa3b8;
  font-size: 14px;
  line-height: 1.7;
}

.inventory-faq {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.inventory-faq header {
  grid-column: 1 / -1;
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.inventory-faq header span {
  color: #ff9d24;
  font-size: 12px;
  font-weight: 1000;
  letter-spacing: 0.16em;
}

.inventory-faq header strong {
  color: #f4f7ff;
  font-size: 20px;
  font-weight: 1000;
}

.inventory-faq article {
  min-height: 132px;
  padding: 18px;
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(43, 45, 59, 0.95), rgba(31, 33, 45, 0.98));
}

.inventory-faq b {
  color: #ff9d24;
  font-size: 16px;
  font-weight: 1000;
}

.inventory-faq article strong {
  color: #f4f7ff;
  font-size: 15px;
  font-weight: 1000;
}

.inventory-faq p {
  margin: 8px 0 0;
  color: #9aa3b8;
  font-size: 13px;
  line-height: 1.65;
}

@keyframes caseFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@media screen and (max-width: 1120px) {
  .inventory-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .inventory-tabs {
    grid-template-columns: repeat(3, 1fr);
  }

  .inventory-toolbar,
  .delivery-panel {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media screen and (max-width: 780px) {
  .inventory-page {
    padding: 18px 12px 58px;
  }

  .inventory-hero {
    padding: 30px 22px;
    grid-template-columns: 1fr;
  }

  .inventory-auth-card {
    padding: 30px 22px;
    grid-template-columns: 1fr;
  }

  .hero-copy h1 {
    font-size: 42px;
  }

  .hero-case img {
    width: 230px;
  }

  .inventory-stats,
  .inventory-tabs,
  .inventory-grid,
  .inventory-faq {
    grid-template-columns: 1fr;
  }

  .toolbar-actions {
    width: 100%;
  }

  .toolbar-actions button {
    flex: 1;
  }
}
</style>

<style lang="less">
.inventory-page.pk-dota-page {
  padding: 32px 28px 96px !important;
}

.inventory-page.pk-dota-page .inventory-hero,
.inventory-page.pk-dota-page .inventory-auth-card,
.inventory-page.pk-dota-page .inventory-stats,
.inventory-page.pk-dota-page .inventory-toolbar,
.inventory-page.pk-dota-page .inventory-list-loading,
.inventory-page.pk-dota-page .inventory-grid,
.inventory-page.pk-dota-page .empty-inventory,
.inventory-page.pk-dota-page .delivery-panel,
.inventory-page.pk-dota-page .inventory-faq,
.inventory-page.pk-dota-page .inventory-page-nav {
  width: calc(100% - 24px) !important;
  max-width: 1540px !important;
}

.inventory-page.pk-dota-page .inventory-hero {
  min-height: 332px !important;
  border-color: rgba(255, 132, 31, 0.38) !important;
  box-shadow:
    0 28px 90px rgba(0, 0, 0, 0.38),
    inset 0 1px 0 rgba(255, 224, 170, 0.08) !important;
}

.inventory-page.pk-dota-page .hero-case img {
  width: 326px !important;
  filter:
    drop-shadow(0 30px 34px rgba(0, 0, 0, 0.44))
    drop-shadow(0 0 24px rgba(255, 138, 0, 0.28)) !important;
}

.inventory-page.pk-dota-page .inventory-grid {
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)) !important;
  gap: 16px !important;
}

.inventory-page.pk-dota-page .skin-card {
  min-height: 322px !important;
  border-color: rgba(111, 121, 151, 0.22) !important;
}

.inventory-page.pk-dota-page .skin-art {
  height: 166px !important;
}

.inventory-page.pk-dota-page .skin-art img {
  max-width: 188px !important;
  max-height: 136px !important;
}

@media screen and (max-width: 780px) {
  .inventory-page.pk-dota-page {
    padding: 18px 12px 58px !important;
  }

  .inventory-page.pk-dota-page .inventory-hero,
  .inventory-page.pk-dota-page .inventory-auth-card,
  .inventory-page.pk-dota-page .inventory-stats,
  .inventory-page.pk-dota-page .inventory-toolbar,
  .inventory-page.pk-dota-page .inventory-list-loading,
  .inventory-page.pk-dota-page .inventory-grid,
  .inventory-page.pk-dota-page .empty-inventory,
  .inventory-page.pk-dota-page .delivery-panel,
.inventory-page.pk-dota-page .inventory-faq,
  .inventory-page.pk-dota-page .inventory-page-nav {
    width: 100% !important;
  }
}

/* 2026-07-05 exact internal pass: inventory/backpack density. */
.inventory-page.pk-dota-page {
  min-height: calc(100vh - 199px) !important;
  width: 100%;
  max-width: 100%;
  padding: 24px 24px 96px !important;
  box-sizing: border-box;
  overflow-x: clip;
  background:
    radial-gradient(ellipse at 50% 170px, rgba(255, 138, 31, 0.08), transparent 420px),
    linear-gradient(180deg, #20222f 0, #1c1d29 420px, #171923 100%) !important;
}

.inventory-page.pk-dota-page > section {
  min-width: 0;
  box-sizing: border-box;
}

.inventory-page.pk-dota-page .inventory-hero,
.inventory-page.pk-dota-page .inventory-auth-card,
.inventory-page.pk-dota-page .inventory-stats,
.inventory-page.pk-dota-page .inventory-toolbar,
.inventory-page.pk-dota-page .inventory-list-loading,
.inventory-page.pk-dota-page .inventory-grid,
.inventory-page.pk-dota-page .empty-inventory,
.inventory-page.pk-dota-page .delivery-panel,
.inventory-page.pk-dota-page .inventory-faq,
.inventory-page.pk-dota-page .inventory-page-nav {
  width: 100% !important;
  max-width: none !important;
}

.inventory-page.pk-dota-page .inventory-hero {
  min-height: 286px !important;
  padding: 30px 38px !important;
  border: 1px solid #2b2d3c !important;
  border-radius: 8px !important;
  background:
    radial-gradient(ellipse at 72% 50%, rgba(255, 138, 31, 0.14), transparent 48%),
    #20222f !important;
  box-shadow: none !important;
}

.inventory-page.pk-dota-page .hero-copy h1 {
  color: #f4ead8 !important;
  font-size: 50px !important;
}

.inventory-page.pk-dota-page .inventory-stats article,
.inventory-page.pk-dota-page .inventory-toolbar,
.inventory-page.pk-dota-page .delivery-panel,
.inventory-page.pk-dota-page .inventory-faq article {
  border-color: #2b2d3c !important;
  background: #20222f !important;
}

.inventory-page.pk-dota-page .inventory-grid {
  min-width: 0;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)) !important;
  gap: 10px !important;
}

.inventory-page.pk-dota-page .skin-card {
  min-width: 0;
  min-height: 276px !important;
  border: 1px solid rgba(67, 73, 96, 0.68) !important;
  border-radius: 8px !important;
  background:
    radial-gradient(ellipse at 50% 36%, rgba(128, 72, 255, 0.22), transparent 54%),
    #252837 !important;
}

.inventory-page.pk-dota-page .skin-art {
  height: 142px !important;
}

.inventory-page.pk-dota-page .skin-art img {
  max-width: 164px !important;
  max-height: 112px !important;
}

.inventory-page.pk-dota-page .inventory-faq article {
  border-radius: 8px !important;
}

@media screen and (max-width: 980px) {
  .inventory-page.pk-dota-page {
    padding: 24px 20px 96px !important;
  }
}

@media screen and (max-width: 780px) {
  .inventory-page.pk-dota-page {
    padding: 18px 12px 58px !important;
  }

  .inventory-page.pk-dota-page .inventory-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>
